// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 1.0
// updated  : 2026-06-22
// protected: NO  -> P6: list tugas/notifikasi Staff Store (Kacab region & Admin Gudang).
// =============================================================================
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/v2/components/statusMap";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardList, Loader2, RefreshCw, Truck, ClipboardPlus, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function StoreTasksPage() {
  const { api } = useV2Auth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/dashboard/store-tasks");
      if (!r.ok) { toast.error("Gagal memuat tugas store"); return; }
      setData(await r.json());
    } catch (e) { toast.error("Gagal memuat tugas store"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useV2LiveRefresh(load);  // FASE 7B

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return null;

  const items = (data.items || []).filter((i) => filter === "ALL" || i.jenis_tugas === filter);
  const counts = {
    ALL: data.items.length,
    "Permintaan Merchandize": data.items.filter((i) => i.jenis_tugas === "Permintaan Merchandize").length,
    "Pengiriman Store": data.items.filter((i) => i.jenis_tugas === "Pengiriman Store").length,
  };

  return (
    <div className="space-y-5" data-testid="store-tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary" />Tugas Store
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Daftar tugas/notifikasi operasional store (Permintaan & Pengiriman) sesuai cakupan Anda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Cakupan: {data.region || "GLOBAL"}</Badge>
          <Button size="sm" variant="outline" onClick={load} data-testid="store-tasks-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />Muat Ulang
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { k: "ALL", label: "Semua", icon: Inbox },
          { k: "Permintaan Merchandize", label: "Permintaan", icon: ClipboardPlus },
          { k: "Pengiriman Store", label: "Pengiriman", icon: Truck },
        ].filter((t) => t.k === "ALL" || counts[t.k] !== undefined).map((t) => (
          <Button key={t.k} size="sm" variant={filter === t.k ? "default" : "outline"}
            onClick={() => setFilter(t.k)} data-testid={`store-tasks-filter-${t.k.replace(/\s+/g, "-").toLowerCase()}`}>
            <t.icon className="w-4 h-4 mr-1" />{t.label}
            <Badge variant="secondary" className="ml-2">{counts[t.k] ?? 0}</Badge>
          </Button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Jenis Tugas</TableHead><TableHead>Store</TableHead>
              <TableHead>No.</TableHead><TableHead>Status</TableHead>
              <TableHead>Dibuat</TableHead><TableHead>Selesai</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10" data-testid="store-tasks-empty">
                Tidak ada tugas store pada cakupan ini.
              </TableCell></TableRow>
            ) : items.map((i, idx) => (
              <TableRow key={`${i.no}-${idx}`} data-testid={`store-task-row-${idx}`}>
                <TableCell className="text-sm">
                  <span className="inline-flex items-center gap-1">
                    {i.jenis_tugas === "Pengiriman Store" ? <Truck className="w-3.5 h-3.5" /> : <ClipboardPlus className="w-3.5 h-3.5" />}
                    {i.jenis_tugas}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{i.store}</TableCell>
                <TableCell className="font-mono text-xs">{i.no}</TableCell>
                <TableCell>
                  <StatusBadge status={i.status} label={i.status_label} />
                  {i.info && <div className="text-[11px] text-muted-foreground mt-1">{i.info}</div>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmt(i.dibuat)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmt(i.selesai)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>

        {/* Mobile: card list tugas */}
        <div className="md:hidden p-3 space-y-3" data-testid="store-tasks-cards">
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 text-sm" data-testid="store-tasks-empty-m">
              Tidak ada tugas store pada cakupan ini.
            </div>
          ) : items.map((i, idx) => (
            <div key={`${i.no}-${idx}`} className="rounded-xl border p-3 space-y-2" data-testid={`store-task-row-${idx}-m`}>
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-sm font-medium">
                  {i.jenis_tugas === "Pengiriman Store" ? <Truck className="w-3.5 h-3.5" /> : <ClipboardPlus className="w-3.5 h-3.5" />}
                  {i.jenis_tugas}
                </span>
                <StatusBadge status={i.status} label={i.status_label} />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Store: <b className="text-foreground">{i.store}</b></span>
                <span className="font-mono">{i.no}</span>
              </div>
              {i.info && <div className="text-[11px] text-muted-foreground">{i.info}</div>}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground border-t pt-1">
                <span>Dibuat: {fmt(i.dibuat)}</span>
                <span>Selesai: {fmt(i.selesai)}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
